import React from 'react';

const HowToStartPage = ({ onBack, isAuthenticated }) => {
  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 md:p-8">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-8 flex items-center justify-between">
        <h1 className="text-3xl font-bold flex items-center">
          <span className="text-blue-400 mr-3">🚀</span>
          How to Start Grid Trading
        </h1>
        <button
          onClick={onBack}
          className="bg-gray-800 hover:bg-gray-700 px-4 py-2 rounded-lg font-semibold transition-colors border border-gray-600"
        >
          Back
        </button>
      </div>

      <div className="max-w-4xl mx-auto space-y-8">
        
        {/* Intro */}
        <div className="bg-gradient-to-r from-blue-900/40 to-purple-900/40 rounded-xl p-6 border border-blue-500/30">
          <p className="text-lg text-gray-200">
            Follow these simple steps to start the grid trading bot. 
            The bot places buy and sell orders across a price range to capture volatility.
          </p>
        </div>

        {/* Subscription notice */}
        <div className="bg-blue-900/30 border border-blue-500/40 rounded-xl p-5 flex items-start">
          <span className="text-2xl mr-3">💳</span>
          <div>
            <p className="text-white font-semibold mb-1">An active subscription is required to run the bot</p>
            <p className="text-gray-300 text-sm">
              <strong className="text-white">$30 / month</strong>, no performance fees, cancel anytime. You can try the bot live with as little as <strong className="text-white">$20</strong> of trading margin on Hyperliquid. Manage your subscription from <strong className="text-white">⚙️ Settings → Billing</strong> after you sign up.
            </p>
          </div>
        </div>

        {/* Step 1: Create Account */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg">
          <div className="flex items-start">
            <div className="bg-blue-600 rounded-full w-10 h-10 flex items-center justify-center text-xl font-bold flex-shrink-0 mt-1">1</div>
            <div className="ml-6">
              <h2 className="text-xl font-bold mb-2">Create a Hyperliquid Account</h2>
              <p className="text-gray-300 mb-4">
                First, you need an account on Hyperliquid — a crypto trading platform. It's free to sign up!
              </p>
              <ol className="list-decimal list-inside space-y-3 text-gray-300 ml-2">
                <li>
                  Click this link to sign up: <a href="https://app.hyperliquid.xyz/join/ACTUVOAI" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline font-semibold">app.hyperliquid.xyz/join/ACTUVOAI</a>
                </li>
                <li>
                  Connect any crypto wallet you already have (Coinbase Wallet, Binance Web3 Wallet, Trust Wallet, or any other) — or create a new one using just your email.
                </li>
                <li>
                  Don't have a wallet? Download <a href="https://www.coinbase.com/wallet" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">Coinbase Wallet</a> — it's free and beginner-friendly
                </li>
              </ol>
              <div className="mt-4 p-3 bg-blue-900/30 border border-blue-600/50 rounded-lg text-blue-200 text-sm">
                💡 <strong>Tip:</strong> If you're new to crypto, think of your wallet like a digital bank account that you fully control.
              </div>
            </div>
          </div>
        </div>

        {/* Step 2: Deposit Funds */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg">
          <div className="flex items-start">
            <div className="bg-blue-600 rounded-full w-10 h-10 flex items-center justify-center text-xl font-bold flex-shrink-0 mt-1">2</div>
            <div className="ml-6">
              <h2 className="text-xl font-bold mb-2">Add Money to Your Account</h2>
              <p className="text-gray-300 mb-4">
                Deposit USDC (a digital dollar) to start trading. This is the capital the grid bot will use.
              </p>
              <ol className="list-decimal list-inside space-y-3 text-gray-300 ml-2">
                <li>On Hyperliquid, click the <strong className="text-white">"Deposit"</strong> button (top right)</li>
                <li>Send <strong className="text-white">USDC</strong> from your wallet or exchange (like Coinbase or Binance)</li>
                <li>Make sure you're using the <strong className="text-white">Arbitrum network</strong> — deposits to Hyperliquid via Arbitrum are usually free.</li>
              </ol>
              <div className="mt-4 p-3 bg-green-900/30 border border-green-600/50 rounded-lg text-green-200 text-sm">
                💰 <strong>How much to start?</strong> You can start with as little as <strong className="text-white">$20</strong> to test things out.
              </div>
            </div>
          </div>
        </div>

        {/* Step 3: Generate API Keys */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg">
          <div className="flex items-start">
            <div className="bg-blue-600 rounded-full w-10 h-10 flex items-center justify-center text-xl font-bold flex-shrink-0 mt-1">3</div>
            <div className="ml-6">
              <h2 className="text-xl font-bold mb-2">Create a Hyperliquid API Wallet</h2>
              <p className="text-gray-300 mb-4">
                Hyperliquid lets you generate a separate <strong className="text-white">API Wallet</strong> (also called an Agent Wallet) that can place and cancel orders on your behalf <strong className="text-white">but cannot withdraw funds</strong>. This is what actuvo uses — never your main wallet seed phrase or main-wallet private key.
              </p>
              <ol className="list-decimal list-inside space-y-3 text-gray-300 ml-2">
                <li>On Hyperliquid, click <strong className="text-white">"More"</strong> in the top menu</li>
                <li>Select <strong className="text-white">"API"</strong> from the dropdown</li>
                <li>Click <strong className="text-white">"Generate API Wallet"</strong> (sometimes labelled "Generate API Private Key")</li>
                <li>Copy the <strong className="text-white">API Wallet private key</strong> shown on screen — it starts with <code className="text-blue-300 bg-gray-900 px-1 rounded">0x</code> and is ~66 characters long. This is what you paste into actuvo's Settings page.</li>
              </ol>
              <div className="mt-4 p-3 bg-red-900/30 border border-red-600/50 rounded-lg text-red-100 text-sm space-y-2">
                <p><strong>🛑 What NOT to paste into actuvo:</strong></p>
                <ul className="list-disc list-inside ml-2 space-y-1">
                  <li>Your <strong>main wallet seed phrase</strong> (12 / 24 words)</li>
                  <li>Your <strong>main wallet recovery phrase</strong></li>
                  <li>Your <strong>main wallet private key</strong> (the one that signs deposits/withdrawals)</li>
                </ul>
                <p>If you're unsure whether the key you copied is an API Wallet key, <strong>stop and contact support</strong> — don't paste anything. Actuvo only ever needs the API Wallet trading key.</p>
              </div>
              <div className="mt-3 p-3 bg-yellow-900/30 border border-yellow-600/50 rounded-lg text-yellow-200 text-sm">
                ⚠️ <strong>Important:</strong> Never share your API Wallet private key with anyone besides actuvo. You can revoke the API Wallet at any time from Hyperliquid's API page if you suspect it has been compromised.
              </div>
            </div>
          </div>
        </div>

        {/* Step 4: Connect to Our Bot */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg">
          <div className="flex items-start">
            <div className="bg-blue-600 rounded-full w-10 h-10 flex items-center justify-center text-xl font-bold flex-shrink-0 mt-1">4</div>
            <div className="ml-6">
              <h2 className="text-xl font-bold mb-2">Connect Your Account to Our Bot</h2>
              <p className="text-gray-300 mb-4">
                Now let's link your Hyperliquid account to the grid trading bot.
              </p>
              <ol className="list-decimal list-inside space-y-3 text-gray-300 ml-2">
                {isAuthenticated ? (
                  <li>Click the <strong className="text-white">⚙️ Settings</strong> button in your dashboard</li>
                ) : (
                  <li>Log in to your account, then click <strong className="text-white">⚙️ Settings</strong></li>
                )}
                <li>Paste the <strong className="text-white">API Wallet private key</strong> from Step 3 (not your main wallet seed/key)</li>
                <li>Paste your <strong className="text-white">main wallet address</strong> from Step 1 — this is the address that <em>holds</em> your USDC on Hyperliquid (different from the API Wallet)</li>
                <li>Click <strong className="text-white">"Save API Keys"</strong></li>
              </ol>
            </div>
          </div>
        </div>

        {/* Step 5: Start Trading */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg">
          <div className="flex items-start">
            <div className="bg-green-600 rounded-full w-10 h-10 flex items-center justify-center text-xl font-bold flex-shrink-0 mt-1">5</div>
            <div className="ml-6">
              <h2 className="text-xl font-bold mb-2">Start Grid Trading!</h2>
              <p className="text-gray-300 mb-4">
                You're all set! Now just start the bot and let it work.
              </p>
              <ol className="list-decimal list-inside space-y-3 text-gray-300 ml-2">
                <li>Make sure your <strong className="text-white">subscription is active</strong> — the bot will refuse to start without one</li>
                <li>Go to your <strong className="text-white">Dashboard</strong></li>
                <li>Find the <strong className="text-white">"Grid Bot"</strong> section</li>
                <li>Click the green <strong className="text-white">"Start Bot"</strong> button</li>
                <li>The bot will place grid orders across the price range automatically</li>
              </ol>
              <div className="mt-4 p-3 bg-purple-900/30 border border-purple-600/50 rounded-lg text-purple-200 text-sm">
                <strong>How it works:</strong> The bot places buy orders below the current price and sell orders above it. When price moves through these levels, it captures the spread as profit.
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <span className="mr-2">❓</span> Common Questions
          </h2>
          <div className="space-y-4">
            <div>
              <p className="text-white font-semibold">Is my money safe?</p>
              <p className="text-gray-400 text-sm">Yes! Your funds stay in YOUR Hyperliquid account. We can only trade, never withdraw.</p>
            </div>
            <div>
              <p className="text-white font-semibold">Can I stop at any time?</p>
              <p className="text-gray-400 text-sm">Absolutely! Just click "Stop Trading" and the bot will stop opening new positions.</p>
            </div>
            <div>
              <p className="text-white font-semibold">What if I want to close a trade myself?</p>
              <p className="text-gray-400 text-sm">You have full control! Close a single position or all positions directly from your dashboard. You can also sync individual positions or sync all to match the leader.</p>
            </div>
            <div>
              <p className="text-white font-semibold">How much does the platform cost?</p>
              <p className="text-gray-400 text-sm">$30 per month, no performance fees. You keep 100% of your trading profits. Cancel anytime from ⚙️ Settings → Billing.</p>
            </div>
          </div>
        </div>

        <div className="text-center pt-8">
          <button
            onClick={onBack}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 px-8 py-3 rounded-lg font-bold text-lg transition-all shadow-lg"
          >
            Got it, Let's Go!
          </button>
        </div>

      </div>
    </div>
  );
};

export default HowToStartPage;
